<template>
  <div class="column alignCentre fullWidth">
    <!-- Drag & Drop and Upload Button Area -->
    <div
      class="drop-area column alignCentre"
      @click="triggerFileInput"
      @dragover.prevent
      @dragenter.prevent="handleDragEnter"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
    >
      <svg class="smaller">
        <use href="@/assets/icons/iconset.svg#upload"></use>
      </svg>
      <div class="alignCentre">
        <p class="nomargintopbottom">Drag your file here or click to upload</p>
        <div class="or">or</div>
        <UnitButton
          variant="secondary"
          size="small"
          type="button"
          @click.stop="triggerCameraInput"
          v-haptic
        >
          <svg class="compactImg">
            <use href="@/assets/icons/iconset.svg#camera_compact"></use>
          </svg>
          Capture image
        </UnitButton>
      </div>
    </div>

    <!-- Preview Area -->
    <div class="previewArea" v-if="imagePreview">
      <div class="image-container">
        <div class="imageActionsCont innerSticker" @click.stop>
          <svg class="smallEdit compactImg" v-haptic @click="editImage">
            <use href="@/assets/icons/iconset.svg#edit_compact"></use>
          </svg>
          <svg class="smallClose compactImg" v-haptic @click="removeImage">
            <use href="@/assets/icons/iconset.svg#trash_compact"></use>
          </svg>
        </div>
        <img class="previewedImg" :src="imagePreview" alt="" />
      </div>
    </div>

    <!-- Cropper -->
    <Overlay
      v-if="isCropping"
      :visible="isCropping"
      :title="'Crop image'"
      @close="closeOverlay"
    >
      <template #before-buttons>
        <ImageCrop
          v-if="currentImageSrc"
          :imageSrc="currentImageSrc"
          @updateImage="updateImage"
          @cancelCrop="isCropping = false"
        />
      </template>
    </Overlay>

    <!-- Hidden Inputs -->
    <input
      type="file"
      accept="image/*"
      @change="handleFileSelection"
      ref="fileInput"
      style="display: none"
    />
    <input
      type="file"
      accept="image/*"
      capture="environment"
      @change="handleFileSelection"
      ref="cameraInput"
      style="display: none"
    />
  </div>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from "vue";
import ImageCrop from "@/components/GenericComponents/ImageCrop.vue";
import Overlay from "@/components/GenericComponents/Overlay.vue";
import { toast } from "vue3-toastify";
import UnitButton from "@/components/GenericComponents/UnitButton.vue";

export default {
  name: "ImageUploadSingleImg",
  components: {
    Overlay,
    ImageCrop,
    UnitButton,
  },
  props: {
    existingImages: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["updateImages", "logoRemoved"],

  setup(props, { emit }) {
    const fileInput = ref(null);
    const cameraInput = ref(null);
    const selectedFile = ref(null);
    const imagePreview = ref(null);
    const isCropping = ref(false);
    const currentImageSrc = ref(null);

    watch(
      () => props.existingImages,
      (newVal) => {
        if (Array.isArray(newVal) && newVal.length > 0) {
          console.log("Existing image found:", newVal[0]);
          imagePreview.value = newVal[0];
        }
      },
      { immediate: true }
    );

    const closeOverlay = async () => {
      console.log("Closing image crop overlay.");
      document.body.classList.remove("no-scroll");
      isCropping.value = false;
    };

    const triggerFileInput = () => {
      console.log("File input triggered.");
      fileInput.value.click();
    };

    const triggerCameraInput = () => {
      console.log("Camera input triggered.");
      cameraInput.value.click();
    };

    const handleFileSelection = async (event) => {
      console.log("File selection event triggered.");
      const newFile = event.target.files ? event.target.files[0] : null;

      if (newFile) {
        console.log("File selected:", newFile.name);

        // Check file size for GIFs
        if (newFile.type === "image/gif") {
          const sizeInMB = newFile.size / (1024 * 1024);
          if (sizeInMB > 5) {
            toast.error(
              `File too large (${sizeInMB.toFixed(
                2
              )} MB). GIF files are limited to 5MB maximum.`,
              { autoClose: 5000 }
            );
            event.target.value = ""; // Reset file input
            return;
          }
        }

        // Resize the image
        const resized = await resizeImage(newFile, 1920);
        selectedFile.value = resized.file;
        imagePreview.value = resized.url;
        emitUpdateImages();
      } else {
        console.log("No file selected.");
      }

      event.target.value = ""; // Reset file input
    };

    const resizeImage = (file, maxDimension) => {
      return new Promise((resolve, reject) => {
        // Skip processing for animated GIFs
        if (file.type === "image/gif") {
          console.log(
            "Animated GIF detected - skipping compression to preserve animation"
          );
          resolve({ file, url: URL.createObjectURL(file) });
          return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
          let width = img.width;
          let height = img.height;

          // Calculate the new dimensions
          if (width > height) {
            if (width > maxDimension) {
              height *= maxDimension / width;
              width = maxDimension;
            }
          } else {
            if (height > maxDimension) {
              width *= maxDimension / height;
              height = maxDimension;
            }
          }

          // Log original file size
          const originalSize = file.size / (1024 * 1024);
          console.log(`Original image size: ${originalSize.toFixed(2)} MB`);

          // Create a canvas and draw the image into it
          const canvas = document.createElement("canvas");
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          // Set compression quality based on file type
          const compressionQuality = 1.0; // 100% quality - no JPEG compression
          const mimeType =
            file.type === "image/png" ? "image/png" : "image/jpeg";
          const quality = file.type === "image/png" ? 1.0 : compressionQuality;

          // Convert the canvas back to a Blob with compression
          canvas.toBlob(
            (blob) => {
              // Log compressed size
              const compressedSize = blob.size / (1024 * 1024);
              console.log(
                `Compressed image size: ${compressedSize.toFixed(2)} MB`
              );
              console.log(
                `Compression ratio: ${(
                  (1 - compressedSize / originalSize) *
                  100
                ).toFixed(0)}%`
              );

              const resizedFile = new File([blob], file.name, {
                type: mimeType,
              });
              const resizedUrl = URL.createObjectURL(resizedFile);
              resolve({ file: resizedFile, url: resizedUrl });
              URL.revokeObjectURL(url); // Clean up
            },
            mimeType,
            quality
          );
        };

        img.onerror = (err) => {
          URL.revokeObjectURL(url); // Clean up
          reject(err);
        };

        img.src = url;
      });
    };

    const emitUpdateImages = () => {
      console.log("Emitting updateImages event with selected file.");
      emit("updateImages", [
        { file: selectedFile.value, url: imagePreview.value },
      ]);
    };

    const handleDragEnter = (event) => {
      console.log("Drag enter event triggered.");
      event.target.classList.add("drag-over");
    };

    const handleDragLeave = (event) => {
      console.log("Drag leave event triggered.");
      event.target.classList.remove("drag-over");
    };

    const handleDrop = async (event) => {
      console.log("Drop event triggered.");
      const newFile = event.dataTransfer.files
        ? event.dataTransfer.files[0]
        : null;

      if (newFile) {
        console.log("File dropped:", newFile.name);
        // Resize the image
        const resized = await resizeImage(newFile, 1920);
        selectedFile.value = resized.file;
        imagePreview.value = resized.url;
        emitUpdateImages();
      } else {
        console.log("No file dropped.");
      }
    };

    const removeImage = async () => {
      console.log("Remove image triggered.");
      if (imagePreview.value) {
        if (typeof imagePreview.value === "string") {
          console.log(
            "Emitting logoRemoved event for URL:",
            imagePreview.value
          );
          emit("logoRemoved", imagePreview.value);
        } else {
          URL.revokeObjectURL(imagePreview.value);
          console.log("Object URL revoked for:", imagePreview.value);
        }
        imagePreview.value = null;
        selectedFile.value = null;
        emit("updateImages", []);
        console.log("Image removed and updateImages emitted with empty array.");
      }
    };

    const editImage = () => {
      console.log("Edit image triggered.");
      document.body.classList.add("no-scroll");
      isCropping.value = true;
      currentImageSrc.value = imagePreview.value;
      console.log("Image cropping started for:", imagePreview.value);
    };

    const updateImage = async ({ file, url }) => {
      console.log("Updating image after crop.");
      // Resize the cropped image
      const resized = await resizeImage(file, 1920);
      selectedFile.value = resized.file;
      imagePreview.value = resized.url;
      emitUpdateImages();
      isCropping.value = false;
      console.log("Image updated successfully.");
    };

    onMounted(() => {
      if (props.existingImages.length > 0) {
        console.log(
          "On mounted: Existing image detected:",
          props.existingImages[0]
        );
        imagePreview.value = props.existingImages[0];
      }
    });

    onUnmounted(() => {
      if (imagePreview.value && imagePreview.value instanceof File) {
        URL.revokeObjectURL(imagePreview.value);
        console.log(
          "On unmounted: Object URL revoked for:",
          imagePreview.value
        );
      }
    });

    return {
      fileInput,
      cameraInput,
      triggerFileInput,
      triggerCameraInput,
      handleFileSelection,
      imagePreview,
      handleDragEnter,
      handleDragLeave,
      handleDrop,
      removeImage,
      isCropping,
      editImage,
      updateImage,
      currentImageSrc,
      closeOverlay,
    };
  },
};
</script>

<style scoped>
.drop-area {
  border: 1px dashed var(--color-border-dropzone);
  padding: var(--space-5);
  text-align: center;
  border-radius: var(--radius-sm);
  width: 100%;
}

.drop-area.drag-over {
  border-color: var(--color-feedback-success-border-strong);
  background-color: var(--color-feedback-success-surface-strong);
}

.previewArea {
  display: flex;
  width: 100%;
  overflow-x: auto;
  gap: var(--space-4);
  padding: var(--space-4);
  box-shadow: var(--shadow-surface-recessed);
  border-radius: var(--radius-lg);
}

.image-container {
  position: relative;
  min-width: 100%;
  width: 100%;
  height: 150px;
  display: flex;
  border-radius: var(--radius-md);
  overflow: hidden;
}

svg.smallClose {
  position: initial;
}
.smallEdit {
  top: var(--space-2);
  transition: opacity 0.15s, transform 0.15s;
  opacity: 0.8;
}

.smallEdit:hover {
  opacity: 1;
  transform: translateY(1px);
}

.previewedImg {
  object-fit: cover;
  width: 100%;
}
.alignCentre {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
@media only screen and (max-width: 490px) {
  .image-container {
    min-width: 100%;
    width: 100%;
    height: 170px;
  }
}
</style>
